import type { ApiResponse } from "../../utils/apiClient";
import { adminApi, adminSilentApi, userSilentApi } from "../../utils/apiInstances";

export type AuthContext = "admin" | "user";

/**
 * Unified function to fetch notifications.
 */
export const handleGetNotifications = async <TData = unknown>(
    context: AuthContext,
    params: Record<string, unknown> = {}
): Promise<ApiResponse<TData>> => {
    try {
        const defaultParams: Record<string, unknown> = {
            per_page: context === "admin" ? 10 : 5,
        };

        const queryParams: Record<string, unknown> = { ...defaultParams, ...params };

        const queryString = Object.keys(queryParams)
            .filter((key) => queryParams[key] !== undefined && queryParams[key] !== null && queryParams[key] !== "")
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(queryParams[key]))}`)
            .join("&");

        const api = context === "admin" ? adminSilentApi : userSilentApi;
        const endpoint = context === "admin" ? "/admin/admin-notifications" : "/user/notifications";
        const uri = `${endpoint}${queryString ? `?${queryString}` : ""}`;

        const response = await api<TData>("GET", uri);
        return response;
    } catch (error) {
        console.error(`Error fetching notifications (${context}):`, error);
        throw error;
    }
};

/**
 * Admin-only: Create a notification.
 */
export const handleAdminNotiCreate = async (
    userData: Record<string, unknown>,
    onSuccess: (response: Record<string, unknown>) => void,
    onError?: (error: unknown) => void
) => {
    try {
        const response = await adminApi("POST", "/admin/admin-notifications", userData);
        onSuccess(response as Record<string, unknown>);
    } catch (error) {
        if (onError) onError(error);
    }
};

export const handleMarkNotificationRead = async (
    context: AuthContext,
    id: string
): Promise<ApiResponse<Record<string, unknown>>> => {
    const api = context === "admin" ? adminSilentApi : userSilentApi;
    const endpoint = context === "admin"
        ? `/admin/admin-notifications/${encodeURIComponent(id)}/read`
        : `/user/notifications/${encodeURIComponent(id)}/read`;

    return api<Record<string, unknown>>("PATCH", endpoint);
};

export const handleMarkAllNotificationsRead = async (
    context: AuthContext
): Promise<ApiResponse<Record<string, unknown>>> => {
    const api = context === "admin" ? adminSilentApi : userSilentApi;
    const endpoint = context === "admin"
        ? "/admin/admin-notifications/read-all"
        : "/user/notifications/read-all";

    return api<Record<string, unknown>>("PATCH", endpoint);
};
